import React, { createContext, useContext, useState, useEffect } from "react";
import {
  getAuth,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import { app } from "@/services/firebase";

const auth = getAuth(app);
const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    return onAuthStateChanged(auth, async (u) => {
      if (u) {
        const token = await u.getIdTokenResult();
        setIsAdmin(!!token.claims.admin);
        setUser(u);
      } else {
        setUser(null);
        setIsAdmin(false);
      }
      setLoading(false);
    });
  }, []);

  // Real Firebase Auth. Only accounts with the `admin` custom claim may enter.
  const login = async ({ email, password }) => {
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const token = await cred.user.getIdTokenResult();
      if (!token.claims.admin) {
        await signOut(auth);
        return { ok: false, error: "This account does not have admin access." };
      }
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e.code || "Login failed" };
    }
  };

  const logout = () => signOut(auth);

  return (
    <AdminAuthContext.Provider
      value={{
        user,
        session: user, // alias: user.email is used in the layout
        isAuthenticated: isAdmin,
        loading,
        login,
        logout,
      }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
}

export const useAdminAuth = () => {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be inside AdminAuthProvider");
  return ctx;
};
